<template>
  <div v-if="!isMobile" class="w-full bg-[white] overflow-x-hidden">
    <section class="h-[70vh] relative bg-cover bg-center bg-no-repeat bg-cover"
      :style="{ backgroundImage: `url(${sourceProduct})` }">
      <div class="absolute inset-0 bg-black/40"></div>
      <div class="relative z-10 h-full flex flex-col justify-center pl-[9%]">
        <div class="font-bold text-[3.1vw] text-white w-[55%] text-left">
          {{ heroTitle }}
        </div>
      </div>
    </section>
    <section>
      <div class="w-screen mx-auto">
        <div class="w-[81vw] mx-auto mb-8 text-left mt-[3vw]">
          <p class="text-left text-black font-bold text-[2.6vw] mt-3.0vw">
            {{ illustrationsTitle }}
          </p>
          <p class="text-left text-black mx-auto mt-[1.2vw] text-[1.2vw] font-medium leading-[1.4]">
            {{ illustrationsDescription }}
          </p>
        </div>

        <div class="w-[81vw] mx-auto grid grid-cols-3 mt-[2vw] md:grid-cols-3 gap-[20px]">
          <div class="p-[1.7vw_1.2vw] bg-[#f3F8FF] relative group hover:shadow-lg transition-shadow duration-300 flex flex-col"
               v-for="(item, index) in categorys"
               :key="index"
               :class="{ 'cursor-pointer': item.showMoreInfo }"
               @click="item.showMoreInfo && goToCategoryDetail(item.id)">
            <div class="font-bold text-[1.3vw] text-black">
              {{ item.text }}
            </div>
            <div class="font-medium text-[1.1vw] text-[#666666] leading-[1.4] mt-[1vw] flex-1">
              {{ item.subText }}
            </div>
            <div v-if="item.showMoreInfo"
                 class="mt-[1.2vw] text-[#007bff] text-[1vw] font-medium flex items-center justify-end gap-[0.3vw] group-hover:gap-[0.6vw] transition-all duration-300">
              <span>{{ t('sourceProduct.clickForMoreInfo') }}</span>
              <svg class="w-[1vw] h-[1vw]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </div>
          </div>
        </div>
        <ImageGallery :imageItems="imageItems" imageHeightClass="h-[230px]" :columns="4" />
      </div>
    </section>
    <section class="mt-[4vw] py-[3vw]">
      <div class="w-[81vw] mx-auto text-left">
        <h2 class="text-[2.6vw] font-bold text-black mb-[2vw]">{{ faqTitle }}</h2>
        <div class="space-y-[1vw]">
          <div v-for="(faq, index) in faqItems" :key="index" class="bg-[#f3F8FF] p-[1.5vw]">
            <h3 class="text-[1.3vw] font-semibold text-black mb-[0.5vw]">{{ faq.question }}</h3>
            <p class="text-[1.1vw] text-[#444] leading-[1.5]">
              <template v-if="index === 1">
                {{ faq.answer }}
                <router-link to="/core-offers/source-product" class="text-[#007bff] hover:underline font-medium ml-1">{{ faq.linkText1 }}</router-link>
                {{ commonOr }}
                <router-link
                  id="cta-qa2-contactus"
                  to="/contact-us"
                  class="text-[#007bff] hover:underline font-medium ml-1"
                  @click="handleQAContactClick">{{ faq.linkText2 }}</router-link>
                {{ faq.linkSuffix }}
              </template>
              <template v-else-if="index === 6">
                {{ faq.answer }}
                <router-link
                  id="cta-qa7-contactus"
                  to="/contact-us"
                  class="text-[#007bff] hover:underline font-medium"
                  :class="{ 'ml-1': faq.answer }"
                  @click="handleQAContactClick">{{ faq.linkText }}</router-link>
                {{ faq.linkSuffix }}
              </template>
              <template v-else>
                {{ faq.answer }}
              </template>
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>

  <!-- Mobile版本 -->
  <MobileSourceProduct v-if="isMobile" />
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from "vue";
import { imageSources } from "../utils/imageData";
import sourceProduct from "../assets/banner_source_product.png";
import ImageGallery from '../components/ImageGallery.vue';
import { pushToDataLayer } from '../utils/gtm';
import { useChatWidget } from '../utils/chatWidget';
import { saveCTAInfo, CTA_CONFIG } from '../utils/ctaTracking';
import { useRouter } from "vue-router";
import { useHead } from '@vueuse/head';
import { useI18n } from 'vue-i18n';
import { useDeviceDetection } from "@/utils/deviceDetection";
import MobileSourceProduct from "../components/MobileSourceProduct.vue";

const { t, locale, messages } = useI18n();

const { isMobile } = useDeviceDetection();

// 获取国际化的 hero 标题
const heroTitle = computed(() => {
  return messages.value[locale.value]?.sourceProduct?.hero?.title || 'Sourcing Product';
});

// 获取国际化的 illustrations 数据
const illustrationsTitle = computed(() => {
  return messages.value[locale.value]?.sourceProduct?.illustrations?.title || 'Sourcing Product Illustrations';
});

const illustrationsDescription = computed(() => {
  return messages.value[locale.value]?.sourceProduct?.illustrations?.description || '';
});

// 获取国际化的 FAQ 标题
const faqTitle = computed(() => {
  return messages.value[locale.value]?.sourceProduct?.faq?.title || 'Frequently Asked Questions (Q&A)';
});

// 获取国际化的 common.or
const commonOr = computed(() => {
  return messages.value[locale.value]?.common?.or || 'or';
});

// 合并图片源和国际化文本
// 合并图片源和国际化文本
const imageItems = computed(() => {
  const textItems = messages.value[locale.value]?.sourceProduct?.imageItems || [];
  console.log('🖼️ Image items debug:');
  console.log('imageSources length:', imageSources.length);
  console.log('textItems length:', textItems.length);
  console.log('First few textItems:', textItems.slice(0, 3));
  
  return imageSources.map((src, index) => {
    const textItem = textItems[index];
    const result = {
      src,
      alt: textItem?.alt || `Image ${index + 1}`,
      caption: textItem?.caption || ''
    };
    
    // 调试特定索引的数据
    if (index < 5 || index > 25) {
      console.log(`Image ${index}:`, {
        hasTextItem: !!textItem,
        alt: result.alt,
        caption: result.caption
      });
    }
    
    return result;
  });
});

// 获取国际化的 categorys
const categorys = computed(() => {
  return messages.value[locale.value]?.sourceProduct?.categorys || [];
});

// 获取国际化的 FAQ items
const faqItems = computed(() => {
  return messages.value[locale.value]?.sourceProduct?.faq?.items || [];
});

// 动态多语言 SEO
import { getSEO } from '../utils/seoConfig';
const seo = computed(() => getSEO(locale.value, 'sourceProduct'));

useHead({
  title: computed(() => seo.value.title),
  meta: [
    { name: 'description', content: computed(() => seo.value.description) },
    { name: 'keywords', content: computed(() => seo.value.keywords) },
    { property: 'og:title', content: computed(() => seo.value.title) },
    { property: 'og:description', content: computed(() => seo.value.description) },
  ],
});


onMounted(() => {
   pushToDataLayer('page_view', {
    page_location: window.location.href,
    page_title: document.title,
    page_referrer: document.referrer,
  });
});
const router = useRouter();
const { openChat } = useChatWidget();

const goToContactUs = () => {
  openChat("SourceProduct");
  pushToDataLayer('click', {
    category: 'SourceProduct',
    label: 'Contact Us',
  });
};

// Q&A 中的 Contact us 链接点击
const handleQAContactClick = () => {
  saveCTAInfo(CTA_CONFIG.QA_CONTACT);
  pushToDataLayer('click', {
    category: 'SourceProduct',
    label: 'Q&A Contact Us',
  });
};

const goToCategoryDetail = (categoryId: string) => {
  if (categoryId === 'fans') {
    router.push('/core-offers/source-product/fan');
    pushToDataLayer('click', {
      category: 'SourceProduct',
      label: 'Fan Category Detail',
    });
  } else if (categoryId === 'filtration-accessories') {
    router.push('/core-offers/source-product/filtration-accessories');
    pushToDataLayer('click', {
      category: 'SourceProduct',
      label: 'Filtration Accessories Category Detail',
    });
  } else if (categoryId === 'belt-conveyor') {
    router.push('/core-offers/source-product/belt-conveyor');
    pushToDataLayer('click', {
      category: 'SourceProduct',
      label: 'Belt Conveyor Category Detail',
    });
  } else if (categoryId === 'flow-components') {
    router.push('/core-offers/source-product/flow-components');
    pushToDataLayer('click', {
      category: 'SourceProduct',
      label: 'Flow Components Category Detail',
    });
  } else if (categoryId === 'silos-tanks') {
    router.push('/core-offers/source-product/silos-tanks');
    pushToDataLayer('click', {
      category: 'SourceProduct',
      label: 'Silos and Tanks Category Detail',
    });
  } else if (categoryId === 'dust-removal') {
    router.push('/core-offers/source-product/dust-removal-system');
    pushToDataLayer('click', {
      category: 'SourceProduct',
      label: 'Dust Removal System Category Detail',
    });
  } else if (categoryId === 'steel-structures') {
    router.push('/core-offers/source-product/steel-structures');
    pushToDataLayer('click', {
      category: 'SourceProduct',
      label: 'Steel Structures Category Detail',
    });
  }
};


</script>

<style scoped>
/* Add optional custom styles here */
</style>
